import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { Search, ChevronDown, Check } from "lucide-react";
import AppContext from "../../context/AppContext";
import { isRTL } from "../../utils/rtl";
import translations from "./Translations";

// Joined button+input "field picker" pill — visual/interaction pattern copied
// from TaskListToolbar's field-picker (src/components/task/TaskListToolbar.js,
// the "Account No." dropdown next to its search box) so every list screen's
// field/scope picker looks and behaves the same: bg-slate-50 trigger button,
// #236DB4 accent, a search box inside the dropdown to filter long option
// lists, and a Check icon on the selected option.
const SearchFieldPicker = ({
  options,
  activeValue,
  onSelect,
  searchPlaceholder = "Find a field...",
  isRtl: isRtlProp,
  buttonTestId,
  buttonClassName = "",
}) => {
  const context = useContext(AppContext);
  const langMode = context?.langMode || "en";
  const isRtl = isRtlProp !== undefined ? isRtlProp : isRTL(langMode);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);

  const activeOption = options.find((o) => o.value === activeValue) || null;

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!isOpen) return;
    const onDocDown = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      setIsOpen(false);
      setQuery("");
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [isOpen]);

  const handleSelect = (value) => {
    onSelect(value);
    setIsOpen(false);
    setQuery("");
  };

  return (
    <div className="relative shrink-0" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        disabled={!options.length}
        className={`h-9 flex max-w-[8.5rem] items-center gap-1.5 px-2 sm:max-w-none sm:px-3 bg-slate-50 border border-slate-200 ${isRtl ? "rounded-r-lg" : "rounded-l-lg"} text-xs sm:text-sm font-medium text-slate-600 hover:bg-slate-100 whitespace-nowrap disabled:opacity-60 ${buttonClassName}`}
        data-testid={buttonTestId}
      >
        <span className="max-w-[110px] truncate">{activeOption?.label}</span>
        <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div
          dir={isRtl ? "rtl" : "ltr"}
          className={`absolute z-40 mt-1.5 w-[min(15rem,calc(100vw-2rem))] bg-white border border-slate-200 rounded-lg shadow-xl ${isRtl ? "right-0" : "left-0"}`}
        >
          <div className="p-2 border-b border-slate-100">
            <div className="relative flex items-center">
              <span
                className={`absolute inset-y-0 flex items-center pointer-events-none text-slate-400 ${
                  isRtl ? "right-2.5" : "left-2.5"
                }`}
              >
                <Search size={14} />
              </span>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className={`w-full h-8 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 ${
                  isRtl ? "pr-8 pl-2.5" : "pl-8 pr-2.5"
                }`}
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filteredOptions.length === 0 && (
              <p className="text-xs text-slate-400 px-3 py-3 text-center">
                {translations[langMode]?.noFieldsMatch ?? "No fields match"}
              </p>
            )}
            {filteredOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => handleSelect(o.value)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs ${
                  isRtl ? "text-right" : "text-left"
                } hover:bg-slate-50 ${
                  o.value === activeValue ? "text-[#236DB4] font-semibold bg-blue-50/60" : "text-slate-700"
                }`}
              >
                <span>{o.label}</span>
                {o.value === activeValue && <Check size={13} className="shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFieldPicker;
